import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Routes, Route } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { collection, getDocs, doc, getDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Helper function to generate URL-friendly slugs from titles
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, and multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

// Fake news articles data
// Ensure all articles have slugs
const mockNewsArticlesRaw = [
  {
    id: '1',
    title: 'Understanding FAFSA Changes for 2025',
    slug: 'understanding-fafsa-changes-for-2025',
    summary: 'New FAFSA regulations take effect this year, impacting financial aid calculations for millions of students. Learn what you need to know.',
    content: `# Understanding FAFSA Changes for 2025

The Free Application for Federal Student Aid (FAFSA) has undergone significant changes in 2025 that will impact how financial aid is calculated and awarded. Here's what you need to know:

## Key Changes

### Simplified Application Process
The new FAFSA form is more streamlined, reducing the number of questions from 108 to just 36. This change aims to make the application process less daunting for students and families.

### Expected Family Contribution (EFC) Replaced
The EFC has been replaced with the Student Aid Index (SAI), which uses a new calculation method. This change affects how your financial need is determined.

### Expanded Pell Grant Eligibility
More students will now qualify for Pell Grants thanks to expanded eligibility criteria. The maximum Pell Grant award has also increased.

## What This Means for You

1. **Earlier Applications**: FAFSA now opens in October instead of January, giving you more time to complete your application.

2. **Tax Information**: The form now uses prior-prior year tax information, which means you'll have more time to gather necessary documents.

3. **Direct Data Sharing**: The IRS Data Retrieval Tool is now mandatory, allowing for more accurate financial information.

## Action Steps

- Complete your FAFSA as soon as possible after October 1st
- Gather necessary tax documents early
- Review your Student Aid Report (SAR) carefully
- Contact your school's financial aid office with questions

Stay informed and proactive to maximize your financial aid opportunities!`,
    author: 'Pocketly Editorial Team',
    date: '2025-01-15',
    category: 'Financial Aid',
    readTime: '5 min read',
  },
  {
    id: '2',
    title: 'Merit Aid: How to Maximize Your Scholarship Opportunities',
    slug: 'merit-aid-how-to-maximize-your-scholarship-opportunities',
    summary: 'Discover strategies to increase your chances of receiving merit-based scholarships from colleges and universities.',
    content: `# Merit Aid: How to Maximize Your Scholarship Opportunities

Merit-based financial aid can significantly reduce your college costs, but competition is fierce. Here's how to position yourself for success:

## Understanding Merit Aid

Merit aid is awarded based on academic achievement, test scores, leadership, and other accomplishments—not financial need. Colleges use merit scholarships to attract top students.

## Strategies to Increase Merit Aid

### 1. Excel Academically
- Maintain a high GPA throughout high school
- Take challenging courses (AP, IB, honors)
- Achieve strong standardized test scores

### 2. Highlight Unique Qualities
- Showcase leadership roles
- Demonstrate community involvement
- Highlight special talents or achievements

### 3. Research Early
- Identify schools that offer generous merit aid
- Understand each school's merit scholarship programs
- Know application deadlines and requirements

### 4. Negotiate When Appropriate
- If you receive better offers from other schools, consider negotiating
- Present your achievements professionally
- Be respectful and realistic in your requests

## Common Merit Scholarship Types

- **Academic Excellence Scholarships**: Based on GPA and test scores
- **Leadership Scholarships**: For demonstrated leadership abilities
- **Talent Scholarships**: For specific skills (music, arts, athletics)
- **National Merit Scholarships**: For high PSAT/NMSQT scores

Start planning early and keep your options open!`,
    author: 'Pocketly Editorial Team',
    date: '2025-01-10',
    category: 'Scholarships',
    readTime: '7 min read',
  },
  {
    id: '3',
    title: 'The Ultimate Guide to Need-Based Financial Aid',
    slug: 'the-ultimate-guide-to-need-based-financial-aid',
    summary: 'Everything you need to know about need-based aid, from eligibility requirements to application tips.',
    content: `# The Ultimate Guide to Need-Based Financial Aid

Need-based financial aid helps make college accessible for students from families with financial constraints. Understanding how it works is crucial for maximizing your aid package.

## What is Need-Based Aid?

Need-based aid is determined by your family's financial situation, not academic performance. Your eligibility is calculated using:

- **Student Aid Index (SAI)**: Your family's expected contribution
- **Cost of Attendance (COA)**: Total cost of attending the school
- **Financial Need**: COA minus SAI

## Types of Need-Based Aid

### Federal Grants
- **Pell Grants**: Awarded to undergraduate students with exceptional financial need
- **Federal Supplemental Educational Opportunity Grant (FSEOG)**: Additional aid for students with exceptional need

### Federal Work-Study
Part-time employment opportunities for eligible students to earn money while attending school.

### Federal Loans
- **Direct Subsidized Loans**: Government pays interest while you're in school
- **Direct Unsubsidized Loans**: Interest accrues immediately

## Maximizing Your Need-Based Aid

1. **Complete the FAFSA Early**: Applications open October 1st
2. **Be Accurate**: Double-check all information before submitting
3. **Apply to Multiple Schools**: Compare aid packages
4. **Appeal if Needed**: If circumstances change, contact financial aid offices

## Special Circumstances

If your family's financial situation has changed due to:
- Job loss
- Medical expenses
- Divorce or separation
- Other significant changes

Contact your school's financial aid office to discuss a professional judgment review.

Remember: Need-based aid can make higher education possible. Don't hesitate to apply!`,
    author: 'Pocketly Editorial Team',
    date: '2025-01-05',
    category: 'Financial Aid',
    readTime: '6 min read',
  },
  {
    id: '4',
    title: 'College Cost Comparison: In-State vs Out-of-State',
    slug: 'college-cost-comparison-in-state-vs-out-of-state',
    summary: 'Breaking down the financial implications of attending an in-state versus out-of-state college.',
    content: `# College Cost Comparison: In-State vs Out-of-State

One of the biggest decisions in college planning is whether to attend an in-state or out-of-state institution. The financial implications can be significant.

## Understanding the Cost Difference

### In-State Tuition
- Public universities charge lower tuition for state residents
- Average in-state tuition: \$10,560 per year
- Includes state subsidies and tax benefits

### Out-of-State Tuition
- Public universities charge higher tuition for non-residents
- Average out-of-state tuition: \$27,020 per year
- Can be 2-3 times higher than in-state rates

## Factors to Consider

### Academic Program Quality
- Does the out-of-state school offer a program not available in-state?
- What's the reputation and ranking of the program?

### Financial Aid Packages
- Out-of-state schools may offer more merit aid
- Some schools reduce out-of-state tuition for high-achieving students
- Private scholarships may be available

### Career Opportunities
- Consider job markets in different states
- Networking opportunities in different regions
- Long-term career goals

## Strategies to Reduce Out-of-State Costs

1. **Apply for Merit Scholarships**: Many schools offer merit aid to attract out-of-state students
2. **Consider Tuition Reciprocity Programs**: Some states have agreements
3. **Look for Regional Tuition Discounts**: Some schools offer reduced rates for neighboring states
4. **Private Universities**: Often don't differentiate between in-state and out-of-state students

## Making the Decision

Create a comprehensive comparison:
- Total cost of attendance
- Financial aid packages
- Graduation rates
- Job placement rates
- Alumni networks

Sometimes the extra cost is worth it, but make an informed decision!`,
    author: 'Pocketly Editorial Team',
    date: '2024-12-28',
    category: 'College Planning',
    readTime: '8 min read',
  },
  {
    id: '5',
    title: 'Early Decision vs Early Action: Financial Implications',
    slug: 'early-decision-vs-early-action-financial-implications',
    summary: 'Understanding the financial aid differences between early decision and early action admission programs.',
    content: `# Early Decision vs Early Action: Financial Implications

Early admission programs can impact your financial aid opportunities. Here's what you need to know before applying.

## Early Decision (ED)

### How It Works
- Binding commitment: If accepted, you must attend
- Typically apply by November 1st
- Receive decision by mid-December

### Financial Implications
- **No Aid Comparison**: Can't compare financial aid packages from multiple schools
- **Limited Negotiation**: Binding commitment reduces bargaining power
- **Must Commit**: Even if aid package is insufficient

### When ED Makes Sense
- School is your top choice
- Financial aid package is acceptable
- Family can afford the cost regardless of aid

## Early Action (EA)

### How It Works
- Non-binding commitment
- Can apply to multiple schools
- Receive decision early but can wait until May 1st

### Financial Implications
- **Can Compare Packages**: Freedom to compare offers from multiple schools
- **More Flexibility**: Can negotiate and make informed decisions
- **Better Aid Opportunities**: Ability to leverage offers

### When EA Makes Sense
- Want early decision but need flexibility
- Want to compare financial aid packages
- Want to keep options open

## Financial Aid Deadlines

### Early Decision
- May need to commit before seeing aid package
- Deadlines are earlier, so complete FAFSA early
- Often receive aid estimates, not final packages

### Early Action
- More time to review aid packages
- Can wait for regular decision aid offers
- Better ability to negotiate

## Recommendation

Unless your family can afford the full cost regardless of aid, **Early Action is often the better choice financially** because it allows you to:
- Compare multiple aid packages
- Negotiate better offers
- Make an informed financial decision

Think carefully before committing to Early Decision!`,
    author: 'Pocketly Editorial Team',
    date: '2024-12-20',
    category: 'Admissions',
    readTime: '6 min read',
  },
  {
    id: '6',
    title: 'Scholarship Search Strategies That Actually Work',
    slug: 'scholarship-search-strategies-that-actually-work',
    summary: 'Proven methods for finding and winning scholarships to help reduce your college costs.',
    content: `# Scholarship Search Strategies That Actually Work

Finding scholarships takes effort, but with the right strategy, you can uncover opportunities that significantly reduce your college costs.

## Where to Start

### 1. School-Based Scholarships
- Your college's financial aid office
- Academic departments
- Alumni associations
- Athletic departments

### 2. Local Opportunities
- Community foundations
- Local businesses
- Service organizations (Rotary, Kiwanis, etc.)
- High school guidance office

### 3. National Scholarship Databases
- Fastweb
- Scholarships.com
- College Board Scholarship Search
- Peterson's Scholarship Search

### 4. Specialized Scholarships
- Professional associations in your field
- Employers (yours and your parents')
- Religious organizations
- Ethnic and cultural groups

## Winning Strategies

### Start Early
- Begin searching in junior year
- Many scholarships have early deadlines
- Build your profile over time

### Stay Organized
- Create a spreadsheet tracking:
  - Scholarship name
  - Deadline
  - Requirements
  - Application status

### Apply Broadly
- Apply to many scholarships, even smaller ones
- Small awards add up
- Cast a wide net

### Write Strong Essays
- Tailor essays to each scholarship
- Showcase your unique story
- Proofread carefully
- Get feedback from teachers or counselors

### Meet All Requirements
- Follow instructions precisely
- Submit all required materials
- Meet all deadlines

## Common Mistakes to Avoid

1. **Waiting Too Long**: Start early!
2. **Not Reading Requirements**: Carefully review all criteria
3. **Generic Applications**: Customize each application
4. **Missing Deadlines**: Use a calendar system
5. **Giving Up**: Apply to many, even if rejection is common

## Scholarship Application Timeline

- **Junior Year**: Begin researching and building profile
- **Summer Before Senior Year**: Write essays, gather materials
- **Senior Year Fall**: Submit early applications
- **Senior Year Spring**: Continue applying
- **Throughout Year**: Maintain good grades and involvement

Remember: Every dollar in scholarships reduces your need for loans. Make scholarship searching a priority!`,
    author: 'Pocketly Editorial Team',
    date: '2024-12-15',
    category: 'Scholarships',
    readTime: '10 min read',
  },
  {
    id: '7',
    title: 'How to Appeal Your Financial Aid Award',
    slug: 'how-to-appeal-your-financial-aid-award',
    summary: 'Learn the proper steps to negotiate and appeal your financial aid package to potentially receive more assistance.',
    content: `# How to Appeal Your Financial Aid Award

Receiving a financial aid award that doesn't meet your family's needs can be frustrating, but you have options. Many families successfully appeal their aid packages by following the right process.

## When to Appeal

Appealing makes sense when:
- Your family's financial situation has changed significantly
- You have new information that wasn't included in your original application
- You received better offers from other schools that you can use as leverage
- There were errors or omissions in your original FAFSA

## Types of Appeals

### Professional Judgment Review
If your family's circumstances have changed due to:
- Job loss or reduced income
- Medical expenses
- Divorce or separation
- Death of a family member
- Other significant financial changes

Contact your school's financial aid office to request a professional judgment review.

### Merit Appeal
If you've received better aid offers from comparable schools, you can:
- Present those offers professionally
- Highlight your achievements and value to the institution
- Request a reconsideration based on your merit

### Need-Based Appeal
If your family's financial situation wasn't accurately represented:
- Provide documentation of special circumstances
- Explain any unique financial challenges
- Show how your situation differs from what the FAFSA shows

## Steps to Appeal

### 1. Gather Documentation
- Updated tax returns
- Medical bills or insurance statements
- Job loss notices or unemployment benefits
- Bank statements showing changes
- Letters from other schools with better offers (if applicable)

### 2. Write a Formal Letter
Your appeal letter should:
- Be respectful and professional
- Clearly explain your situation
- Provide specific financial details
- Include supporting documentation
- Request a specific amount or adjustment

### 3. Contact the Financial Aid Office
- Find the right person to contact (usually the director)
- Schedule an appointment if possible
- Submit your appeal in writing
- Follow up appropriately

### 4. Be Patient and Persistent
- Appeals can take time to process
- Be prepared to provide additional information
- Follow up if you don't hear back within a reasonable timeframe

## Timing Your Appeal

- **Early Appeals**: Submit as soon as possible after receiving your award
- **Deadlines**: Check your school's deadline for appeals
- **Better Timing**: Appeals may be more successful earlier in the award cycle

## What to Avoid

- Being aggressive or demanding
- Comparing your situation to others'
- Making threats about withdrawing
- Submitting incomplete information
- Waiting too long to appeal

## Tips for Success

1. **Be Specific**: Provide concrete numbers and documentation
2. **Be Realistic**: Request reasonable increases based on your situation
3. **Be Polite**: Professional communication goes a long way
4. **Be Prepared**: Have all documentation ready
5. **Be Flexible**: Consider alternative aid sources if the appeal doesn't work

Remember: Most schools want to help students attend, so a well-prepared, professional appeal can often yield positive results.`,
    author: 'Pocketly Editorial Team',
    date: '2024-12-10',
    category: 'Financial Aid',
    readTime: '8 min read',
  },
  {
    id: '8',
    title: 'Student Loan Repayment Plans: A Complete Guide',
    slug: 'student-loan-repayment-plans-complete-guide',
    summary: 'Understanding the different federal student loan repayment options and choosing the best plan for your financial situation.',
    content: `# Student Loan Repayment Plans: A Complete Guide

Navigating student loan repayment can be overwhelming, but understanding your options is crucial for managing your debt effectively.

## Types of Federal Loan Repayment Plans

### Standard Repayment Plan
- **Payment Term**: 10 years (up to 30 years for consolidated loans)
- **Monthly Payment**: Fixed amount each month
- **Best For**: Borrowers who can afford higher monthly payments
- **Pros**: Pay off loans fastest, least interest paid
- **Cons**: Higher monthly payments than other plans

### Graduated Repayment Plan
- **Payment Term**: 10 years (up to 30 years for consolidated loans)
- **Monthly Payment**: Starts low and increases every 2 years
- **Best For**: Borrowers who expect income to increase over time
- **Pros**: Lower initial payments
- **Cons**: More interest paid overall than standard plan

### Extended Repayment Plan
- **Payment Term**: Up to 25 years
- **Monthly Payment**: Fixed or graduated
- **Best For**: Borrowers with large loan balances
- **Pros**: Lower monthly payments
- **Cons**: Much more interest paid over time

### Income-Driven Repayment Plans

These plans base your monthly payment on your income and family size:

#### Income-Based Repayment (IBR)
- **Payment**: 10-15% of discretionary income
- **Term**: 20-25 years
- **Eligibility**: Must demonstrate financial hardship

#### Pay As You Earn (PAYE)
- **Payment**: 10% of discretionary income
- **Term**: 20 years
- **Eligibility**: Must be a new borrower after Oct. 1, 2007

#### Revised Pay As You Earn (REPAYE)
- **Payment**: 10% of discretionary income
- **Term**: 20-25 years
- **Eligibility**: Available to all Direct Loan borrowers

#### Income-Contingent Repayment (ICR)
- **Payment**: 20% of discretionary income or fixed payment
- **Term**: 25 years
- **Eligibility**: Available to all Direct Loan borrowers

## Choosing the Right Plan

### Consider Your Income
- **Low Income**: Income-driven plans may be best
- **Stable, Good Income**: Standard plan might save you money
- **Variable Income**: Income-driven plans provide flexibility

### Consider Your Goals
- **Quick Payoff**: Standard or Graduated plans
- **Lower Monthly Payments**: Income-driven or Extended plans
- **Loan Forgiveness**: Income-driven plans (after 20-25 years)

### Consider Your Career
- **Public Service**: Public Service Loan Forgiveness (PSLF) available with income-driven plans
- **Private Sector**: Standard plan may be most cost-effective

## Switching Repayment Plans

You can change your repayment plan at any time, but:
- Contact your loan servicer
- Understand the impact on your payments
- Consider any fees or changes to interest accrual

## Loan Forgiveness Options

### Public Service Loan Forgiveness (PSLF)
- Work full-time for government or qualifying non-profit
- Make 120 qualifying payments
- Remain on an income-driven repayment plan
- Remaining balance forgiven tax-free

### Teacher Loan Forgiveness
- Teach full-time for 5 consecutive years
- Work in low-income school or educational service agency
- Up to \$17,500 forgiven for highly qualified teachers

### Income-Driven Forgiveness
- After 20-25 years of payments on income-driven plans
- Remaining balance forgiven
- **Note**: May be taxable as income

## Tips for Repayment Success

1. **Make Extra Payments**: If possible, pay more than the minimum
2. **Target High-Interest Loans First**: Pay extra on loans with highest interest rates
3. **Set Up Auto-Pay**: Often provides a 0.25% interest rate reduction
4. **Stay in Contact**: Keep your loan servicer updated with address changes
5. **Consider Refinancing**: Only if you have good credit and private loans

## What Happens if You Can't Pay

If you're struggling to make payments:
- **Contact Your Servicer Immediately**: They can help you find solutions
- **Request Deferment**: Temporarily postpone payments if eligible
- **Request Forbearance**: Temporarily reduce or stop payments
- **Change Repayment Plans**: Switch to a more affordable plan

## Avoiding Default

Default occurs after 270 days of missed payments and can result in:
- Damage to credit score
- Wage garnishment
- Loss of federal benefits
- Collection fees added to your balance

**Never ignore your loans** - always communicate with your servicer if you're having trouble.

Understanding your repayment options is the first step toward managing your student debt effectively. Choose the plan that best fits your current financial situation, but remember you can always change plans as your circumstances change.`,
    author: 'Pocketly Editorial Team',
    date: '2024-12-05',
    category: 'Financial Aid',
    readTime: '9 min read',
  },
  {
    id: '9',
    title: 'Building a Strong College Application: Grades, Test Scores, and Beyond',
    slug: 'building-strong-college-application-grades-test-scores-beyond',
    summary: 'Discover what colleges really look for and how to craft an application that stands out from the competition.',
    content: `# Building a Strong College Application: Grades, Test Scores, and Beyond

A strong college application requires more than just good grades and test scores. Colleges look for well-rounded students who demonstrate passion, leadership, and potential for growth.

## Core Academic Components

### Grades and GPA
- **Weighted vs. Unweighted**: Understand how your school calculates GPA
- **Course Rigor**: Advanced courses (AP, IB, honors) show academic challenge
- **Trend Matters**: An upward trend in grades is often viewed positively
- **Overall Performance**: Strong performance across subjects is important

### Standardized Test Scores
- **SAT vs. ACT**: Choose the test that plays to your strengths
- **Test-Optional Schools**: Many schools no longer require test scores
- **Superscoring**: Some schools combine your best section scores
- **Preparation**: Consider test prep courses or tutoring if needed

## Beyond the Numbers

### Extracurricular Activities
Colleges want to see:
- **Depth over Breadth**: Commitment to a few activities is better than many shallow involvements
- **Leadership Roles**: Show progression and responsibility
- **Passion and Impact**: Activities should reflect genuine interests
- **Long-term Commitment**: Years of involvement show dedication

### Essays and Personal Statements
Your essays are your voice in the application:
- **Be Authentic**: Write about experiences that truly matter to you
- **Show, Don't Tell**: Use specific examples and stories
- **Answer the Prompt**: Directly address what's being asked
- **Proofread Carefully**: Errors can hurt your credibility
- **Get Feedback**: Have teachers, counselors, or mentors review

### Letters of Recommendation
- **Choose Wisely**: Select teachers who know you well
- **Provide Context**: Give recommenders your resume and achievements
- **Request Early**: Give recommenders plenty of time
- **Follow Up**: Thank recommenders for their time and effort

### Resume or Activities List
- **Be Comprehensive**: Include all significant activities
- **Use Action Verbs**: Describe your impact and contributions
- **Quantify When Possible**: Use numbers to show scale of involvement
- **Be Specific**: Vague descriptions don't help your case

## Holistic Review Factors

### Diversity and Unique Perspectives
Colleges value:
- Geographic diversity
- Socioeconomic diversity
- First-generation college students
- Unique talents and backgrounds
- Different life experiences

### Character and Personal Qualities
Admissions officers look for:
- **Resilience**: Overcoming challenges and setbacks
- **Curiosity**: Genuine interest in learning
- **Empathy**: Caring about others and the community
- **Growth Mindset**: Willingness to learn and improve

## Application Strategy

### Creating Your College List
- **Reach Schools**: 1-2 schools where acceptance is unlikely but possible
- **Target Schools**: 3-5 schools where you have a good chance
- **Safety Schools**: 2-3 schools where acceptance is very likely

### Early Decision vs. Early Action
- **Early Decision**: Binding commitment if accepted
- **Early Action**: Non-binding early application
- **Regular Decision**: Standard application timeline
- Consider financial aid implications of each option

## Common Application Tips

### Completing the Common App
- **Start Early**: Give yourself plenty of time
- **Be Consistent**: Information should match across all sections
- **Proofread Everything**: Errors can hurt your chances
- **Save Regularly**: Don't lose your work

### Supplemental Materials
- **Portfolios**: Only submit if exceptional and relevant
- **Additional Recommendations**: Follow school guidelines
- **Optional Essays**: Complete them if you have something meaningful to say
- **Interview**: Prepare if offered

## What Colleges Really Care About

### Academic Excellence
- Strong grades in challenging courses
- Consistent performance over time
- Intellectual curiosity demonstrated through course selection

### Personal Character
- Integrity and honesty
- Respect for others
- Responsibility and maturity
- Contribution to community

### Potential for Success
- Ability to handle college-level work
- Potential to contribute to campus community
- Likelihood of graduating and succeeding
- Long-term potential and goals

## Mistakes to Avoid

1. **Procrastination**: Waiting until the last minute
2. **Generic Essays**: Not personalizing your application
3. **Ignoring Instructions**: Not following application guidelines
4. **Being Dishonest**: Never lie or exaggerate
5. **Not Proofreading**: Typos and errors hurt credibility
6. **Comparing Yourself**: Focus on your own strengths

## Timeline for Success

### Junior Year
- Maintain strong grades
- Build meaningful extracurricular involvement
- Prepare for standardized tests
- Begin researching colleges

### Summer Before Senior Year
- Visit colleges if possible
- Begin working on essays
- Prepare for senior year
- Continue building your profile

### Senior Year Fall
- Take standardized tests (if needed)
- Complete applications
- Request recommendations
- Submit early applications

### Senior Year Spring
- Submit remaining applications
- Complete FAFSA
- Wait for decisions
- Make your choice

Remember: The best application is one that authentically represents who you are. Focus on being yourself, highlighting your strengths, and demonstrating your potential to contribute to the college community.`,
    author: 'Pocketly Editorial Team',
    date: '2024-11-30',
    category: 'Admissions',
    readTime: '11 min read',
  },
  {
    id: '10',
    title: 'Understanding the True Cost of College Attendance',
    slug: 'understanding-true-cost-college-attendance',
    summary: 'Learn how to calculate the full cost of attending college beyond just tuition, including hidden expenses.',
    content: `# Understanding the True Cost of College Attendance

The sticker price of college tuition is just the beginning. Understanding the full cost of attendance is essential for making informed financial decisions about your education.

## Components of Cost of Attendance (COA)

### Tuition and Fees
- **Tuition**: The cost of instruction and courses
- **Mandatory Fees**: Required fees for services, facilities, and activities
- **Course-Specific Fees**: Additional fees for certain classes or labs
- **Technology Fees**: Often required for online resources

### Room and Board
- **On-Campus Housing**: Dormitory costs vary by school and room type
- **Meal Plans**: Required dining plans (full, partial, or commuter)
- **Off-Campus Housing**: Rent, utilities, groceries if living off-campus
- **Housing Deposits**: Security deposits and application fees

### Books and Supplies
- **Textbooks**: Can cost \$500-\$1,500 per semester
- **Course Materials**: Lab supplies, art supplies, software
- **Technology**: Laptops, tablets, calculators, software licenses
- **Study Materials**: Notebooks, pens, other supplies

### Transportation
- **Commuting Costs**: Gas, parking permits, public transit
- **Travel Home**: Flights, trains, or gas for breaks
- **Study Abroad**: Additional travel costs if applicable

### Personal Expenses
- **Clothing**: Weather-appropriate clothing for new location
- **Personal Care**: Toiletries, haircuts, etc.
- **Entertainment**: Movies, concerts, social activities
- **Emergency Fund**: Unexpected expenses

### Miscellaneous Expenses
- **Health Insurance**: If not covered by parent's plan
- **Fitness Center**: Some schools charge extra
- **Parking**: If you have a car on campus
- **Greek Life**: Fraternity or sorority dues if applicable

## Calculating Your Net Cost

### Cost of Attendance Minus Financial Aid
**Net Cost = COA - Grants - Scholarships - Work-Study**

Your actual out-of-pocket cost may be significantly less than the sticker price.

### Factors That Affect Total Cost

#### Location
- **Urban Areas**: Higher living costs, but better public transit
- **Rural Areas**: Lower living costs, but may need a car
- **Out-of-State**: Higher tuition, but may receive merit aid
- **International**: Additional fees and travel costs

#### Living Situation
- **On-Campus**: Predictable costs, meal plans required
- **Off-Campus**: More variable, but potentially more affordable
- **At Home**: Significantly reduces room and board costs

#### Lifestyle Choices
- **Dining Out**: Can add hundreds per month
- **Entertainment**: Varies greatly by student
- **Shopping**: Personal spending varies
- **Travel**: Frequency and distance affect costs

## Hidden Costs to Consider

### First-Year Expenses
- **Orientation Fees**: Often required for new students
- **Dorm Essentials**: Bedding, storage, decorations
- **Laptop/Technology**: May need to purchase
- **Formal Attire**: For special events or interviews

### Ongoing Expenses
- **Greek Life**: Dues, events, housing if applicable
- **Clubs and Organizations**: Membership dues and activities
- **Professional Development**: Conferences, certifications
- **Study Materials**: Beyond required textbooks

### Senior Year Costs
- **Graduation Fees**: Cap, gown, ceremony tickets
- **Job Search Expenses**: Professional clothing, travel for interviews
- **Relocation**: Moving expenses after graduation

## Ways to Reduce Costs

### Academic Strategies
- **Graduate Early**: Take summer courses or extra credits
- **AP/IB Credits**: Reduce required courses
- **Community College**: Take prerequisites at lower cost
- **Co-op Programs**: Earn money while gaining experience

### Financial Strategies
- **Scholarships**: Apply broadly for free money
- **Work-Study**: Earn money to cover expenses
- **Part-Time Job**: Flexible work while in school
- **Budget Carefully**: Track spending and reduce waste

### Lifestyle Strategies
- **Cook at Home**: More affordable than dining out
- **Use Student Discounts**: Many businesses offer discounts
- **Share Expenses**: Roommates can reduce costs
- **Buy Used**: Textbooks, furniture, supplies

## Budgeting for College

### Creating a Budget
1. **List All Income**: Financial aid, work, family contribution
2. **List All Expenses**: Use the COA components as a guide
3. **Track Spending**: Use apps or spreadsheets
4. **Adjust Regularly**: Update as circumstances change

### Managing Your Money
- **Open a Student Account**: Often has lower fees
- **Use Budgeting Apps**: Track spending in real-time
- **Set Spending Limits**: Know your limits for each category
- **Build Emergency Fund**: Save for unexpected expenses

## Comparing College Costs

### Don't Just Look at Tuition
Compare:
- **Total COA**: The full cost of attendance
- **Net Price**: What you'll actually pay
- **Financial Aid Packages**: How much aid each school offers
- **Graduation Rate**: Higher rates mean less wasted money
- **Average Debt**: How much graduates typically owe

### Use Net Price Calculators
Most colleges provide net price calculators that estimate:
- Your expected family contribution
- Estimated financial aid
- Net price you'll pay

These tools give you a more accurate picture than sticker price alone.

## Planning Ahead

### Start Early
- Research costs during junior year
- Compare financial aid offers
- Plan for all expenses, not just tuition
- Save money when possible

### Be Realistic
- Don't underestimate expenses
- Budget for unexpected costs
- Consider four years of costs, not just one
- Factor in potential tuition increases

## Financial Aid Impact

### How Aid Affects Total Cost
- **Grants**: Reduce your cost directly
- **Scholarships**: Reduce your cost directly
- **Work-Study**: Earn money to cover expenses
- **Loans**: Defer cost but increase total paid over time

### Understanding Loan Costs
Remember:
- Loans must be repaid with interest
- Interest accrues over time
- Total repayment is more than amount borrowed
- Consider future earning potential when borrowing

Understanding the true cost of college helps you make informed decisions and avoid financial surprises. Plan carefully, budget wisely, and seek out all available financial aid to minimize your total cost.`,
    author: 'Pocketly Editorial Team',
    date: '2024-11-25',
    category: 'College Planning',
    readTime: '10 min read',
  },
  {
    id: '11',
    title: 'The Complete Guide to Merit Scholarships',
    slug: 'complete-guide-merit-scholarships',
    summary: 'Everything you need to know about merit-based scholarships, from eligibility requirements to application strategies.',
    content: `# The Complete Guide to Merit Scholarships

Merit scholarships can significantly reduce your college costs without the repayment burden of loans. Understanding how to find, apply for, and win merit scholarships is essential for maximizing your financial aid.

## What Are Merit Scholarships?

Merit scholarships are awarded based on:
- **Academic Achievement**: GPA, class rank, course rigor
- **Test Scores**: SAT, ACT, or other standardized tests
- **Talents**: Athletic, musical, artistic abilities
- **Leadership**: Demonstrated leadership in activities
- **Achievements**: Specific accomplishments or awards

**Key Difference**: Merit scholarships are based on achievement, not financial need.

## Types of Merit Scholarships

### Academic Merit Scholarships
- **Presidential Scholarships**: Highest academic honors
- **Dean's Scholarships**: Strong academic performance
- **Honors Program Scholarships**: For program participants
- **Departmental Scholarships**: Specific to your major

### Test Score Scholarships
- **SAT/ACT Based**: Automatic scholarships at certain score thresholds
- **National Merit**: Based on PSAT/NMSQT scores
- **Achievement Scholarships**: For specific test score ranges

### Talent-Based Scholarships
- **Athletic Scholarships**: Varsity sports participation
- **Music Scholarships**: Musical ability and participation
- **Art Scholarships**: Artistic talent and portfolio
- **Theater Scholarships**: Acting and production skills

### Leadership Scholarships
- **Student Government**: Leadership roles in school
- **Community Leadership**: Service and community involvement
- **Organization Leadership**: Leadership in clubs and groups

### Specialized Merit Scholarships
- **State Scholarships**: Often automatic based on grades/test scores
- **Regional Scholarships**: For students from specific areas
- **Demographic Scholarships**: For specific groups (still merit-based)

## How Merit Scholarships Work

### Automatic Awards
Many schools automatically award merit scholarships based on:
- GPA thresholds
- Test score thresholds
- Class rank
- No additional application required

### Competitive Awards
Some merit scholarships require:
- Separate applications
- Essays or personal statements
- Interviews
- Portfolios or auditions
- Recommendations

### Renewable vs. One-Time
- **Renewable**: Continue each year if you meet requirements (GPA, full-time enrollment)
- **One-Time**: Awarded only for first year or single year
- **Requirements**: Understand what you need to maintain scholarship

## Finding Merit Scholarships

### College-Specific Scholarships
- **Admissions Office**: Check their merit scholarship page
- **Financial Aid Office**: Ask about automatic and competitive awards
- **Academic Departments**: Major-specific scholarships
- **Honors Programs**: Special scholarships for participants

### State Merit Scholarships
- **State Education Department**: Often automatic based on criteria
- **State Flagship Programs**: Large merit awards for residents
- **State Grant Programs**: May include merit components

### National Merit Scholarships
- **PSAT/NMSQT**: Qualify through high junior year PSAT scores
- **National Merit Corporation**: Runs the program
- **College-Sponsored Awards**: Many colleges offer additional awards

### External Merit Scholarships
- **Private Foundations**: Many offer merit-based awards
- **Corporations**: Some provide merit scholarships
- **Professional Organizations**: Industry-specific merit awards

## Maximizing Your Merit Scholarship Opportunities

### Academic Excellence
- **Maintain High GPA**: Most merit scholarships have GPA requirements
- **Take Challenging Courses**: AP, IB, honors show academic rigor
- **Strong Test Scores**: Prepare and retake if needed
- **Class Rank**: Top percentage often qualifies automatically

### Building Your Profile
- **Leadership Roles**: Demonstrate leadership in activities
- **Community Service**: Show commitment to helping others
- **Unique Talents**: Develop and showcase special abilities
- **Consistent Involvement**: Long-term commitment shows dedication

### Strategic College Selection
- **Research Merit Programs**: Find schools with generous merit aid
- **Know Your Standing**: Apply where you'll be in top tier
- **Compare Offers**: Don't assume highest-ranked schools give best aid
- **Regional Schools**: Often offer more merit aid to attract students

## Application Strategies

### For Automatic Awards
- **Apply Early**: Some have early deadlines
- **Submit Strong Application**: May factor into initial award
- **Meet Deadlines**: Ensure eligibility
- **Provide Accurate Information**: Mistakes can disqualify you

### For Competitive Awards
- **Start Early**: Many have early deadlines
- **Write Strong Essays**: Tailor to each scholarship
- **Showcase Achievements**: Highlight what makes you unique
- **Get Recommendations**: Choose recommenders who know you well
- **Follow Instructions**: Submit exactly what's requested

### Portfolio/Audition Requirements
- **Prepare Thoroughly**: Practice and refine your work
- **Seek Feedback**: Get input from teachers or mentors
- **Follow Guidelines**: Meet all technical requirements
- **Submit Early**: Don't wait until deadline

## Negotiating Merit Aid

### When to Negotiate
- You have better offers from comparable schools
- Your academic profile has improved since application
- You have additional achievements to report
- The initial offer doesn't meet your needs

### How to Negotiate
- **Be Professional**: Write a respectful, professional letter
- **Provide Evidence**: Include offers from other schools
- **Highlight Value**: Show why you're worth the investment
- **Be Realistic**: Request reasonable increases
- **Timing Matters**: Negotiate early in the process

### What to Expect
- Not all schools negotiate
- Some have strict policies
- Offers may increase or stay the same
- Always be respectful regardless of outcome

## Maintaining Merit Scholarships

### Typical Requirements
- **Minimum GPA**: Usually 3.0-3.5, sometimes higher
- **Full-Time Enrollment**: Must take minimum credit hours
- **Academic Standing**: Must remain in good standing
- **Major Requirements**: Some tied to specific majors

### What Happens if You Fall Below
- **Warning Period**: Some schools give a semester to improve
- **Appeal Process**: May be able to appeal loss of scholarship
- **Partial Loss**: May lose portion of award
- **Complete Loss**: May lose entire scholarship

### Strategies to Maintain
- **Stay Organized**: Track your GPA and requirements
- **Use Resources**: Tutoring, study groups, office hours
- **Prioritize Academics**: Don't let activities hurt grades
- **Seek Help Early**: Address academic problems immediately

## Merit Aid vs. Need-Based Aid

### Merit Aid Advantages
- Based on achievement, not income
- Don't require financial need
- Often renewable if you maintain requirements
- Can be stacked with need-based aid

### Merit Aid Limitations
- May not cover full cost
- Competitive to earn
- Must maintain requirements
- May have restrictions on use

### Combining Both
- You can receive both merit and need-based aid
- Merit aid reduces your need, affecting need-based eligibility
- Total aid package combines all sources
- Always maximize free money (grants, scholarships) before loans

Merit scholarships are an excellent way to reduce college costs through your achievements. Start preparing early, apply strategically, and maintain the standards required to keep your scholarships throughout college.`,
    author: 'Pocketly Editorial Team',
    date: '2024-11-20',
    category: 'Scholarships',
    readTime: '12 min read',
  },
  {
    id: '12',
    title: 'College Visit Checklist: What to Look For and Questions to Ask',
    slug: 'college-visit-checklist-what-look-for-questions-ask',
    summary: 'Make the most of your college visits with this comprehensive guide on what to observe and important questions to ask.',
    content: `# College Visit Checklist: What to Look For and Questions to Ask

College visits are one of the most important steps in choosing the right school. Being prepared and asking the right questions can help you make an informed decision.

## Before You Visit

### Planning Your Visit
- **Schedule in Advance**: Many schools require advance registration
- **Visit During School Year**: See campus when classes are in session
- **Plan Multiple Visits**: If possible, visit at different times
- **Arrange Overnight Stay**: Many schools offer overnight programs

### What to Bring
- **Comfortable Walking Shoes**: You'll do a lot of walking
- **Weather-Appropriate Clothing**: Check the forecast
- **Notebook or Phone**: Take notes and photos
- **Questions List**: Have your questions ready
- **Camera**: Document what you see

### Research First
- Review the school's website
- Check out academic programs of interest
- Look at campus maps
- Read student reviews online
- Prepare specific questions

## During Your Visit

### Campus Tour Essentials

#### Academic Facilities
- **Libraries**: Size, resources, study spaces
- **Labs**: Science, computer, research facilities
- **Classrooms**: Technology, size, environment
- **Study Spaces**: Availability and quality
- **Academic Support**: Tutoring centers, writing labs

#### Residential Life
- **Dorm Rooms**: Size, amenities, cleanliness
- **Common Areas**: Lounges, kitchens, study spaces
- **Bathroom Facilities**: Shared vs. private
- **Residence Hall Atmosphere**: Social, quiet, etc.
- **Housing Options**: Various types available

#### Dining Facilities
- **Meal Plan Options**: Variety and flexibility
- **Food Quality**: Taste, nutrition, variety
- **Dietary Accommodations**: Vegetarian, vegan, allergies
- **Hours of Operation**: Convenience for your schedule
- **Cost**: Is meal plan required, how much?

#### Student Life
- **Student Center**: Activities, services available
- **Campus Activities**: Clubs, organizations, events
- **Greek Life**: Presence, prominence, if interested
- **Athletic Facilities**: Gyms, fields, equipment
- **Arts Facilities**: Theaters, galleries, studios

#### Campus Environment
- **Safety**: Lighting, security presence, emergency systems
- **Cleanliness**: Maintenance, upkeep of facilities
- **Atmosphere**: Formal, casual, competitive, collaborative
- **Diversity**: Visible diversity in student body
- **Size**: Feel manageable or overwhelming?

## Questions to Ask

### Academic Questions
- What is the average class size for introductory courses?
- How accessible are professors outside of class?
- What undergraduate research opportunities are available?
- How do students get advising and academic support?
- What are the most popular majors and why?
- How easy is it to change majors?
- What are the graduation rates?
- What are the job placement rates for graduates?
- Are there study abroad opportunities?
- What internship or co-op programs exist?

### Financial Aid Questions
- What percentage of students receive financial aid?
- What is the average financial aid package?
- Are merit scholarships renewable and what are requirements?
- What is the average student debt upon graduation?
- How does the school help students manage finances?
- Are there work-study opportunities available?
- What happens to aid if family circumstances change?

### Student Life Questions
- What is the campus culture like?
- How do students typically spend their weekends?
- What support services are available for students?
- How diverse is the student body?
- What is the social scene like?
- How active is Greek life (if applicable)?
- What mental health resources are available?
- How safe is the campus and surrounding area?

### Housing and Dining Questions
- Is on-campus housing guaranteed for all four years?
- What are the housing options and costs?
- Can students choose their roommates?
- What is the meal plan like and is it required?
- How good is the food and are there healthy options?
- Are there dining options for dietary restrictions?

### Career and Future Questions
- What career services are available?
- How does the school help with internships?
- What companies recruit on campus?
- What graduate school acceptance rates?
- How strong is the alumni network?
- What mentorship programs exist?

## What to Observe

### Current Students
- **Engagement**: Do students seem engaged and happy?
- **Interactions**: How do students interact with each other?
- **Diversity**: Visible diversity among students
- **Energy**: Campus feel - vibrant, quiet, etc.

### Faculty Interaction
- **Availability**: Do professors seem accessible?
- **Approachability**: Do faculty seem welcoming?
- **Teaching Style**: If sitting in on a class, observe teaching

### Facilities Quality
- **Maintenance**: Are buildings well-maintained?
- **Technology**: Is technology current and accessible?
- **Resources**: Are resources adequate for student needs?

### Campus Culture
- **Values**: What values are emphasized?
- **Community**: Does there seem to be a sense of community?
- **Inclusivity**: Does campus feel welcoming and inclusive?

## After Your Visit

### Immediate Notes
- **Write Down Impressions**: While fresh in your mind
- **Organize Photos**: Label and organize photos taken
- **Compare Schools**: If visiting multiple, compare notes
- **Discuss with Family**: Share your thoughts and feelings

### Follow-Up
- **Thank You Notes**: Email admissions staff who helped
- **Additional Questions**: Contact with any follow-up questions
- **Request Information**: Ask for materials you might have missed
- **Connect**: Follow social media or contact current students

### Decision Making
- **Pros and Cons List**: Create for each school visited
- **Gut Feeling**: Trust your instincts about fit
- **Compare Options**: Use your notes to compare schools
- **Financial Comparison**: Compare costs with impressions

## Red Flags to Watch For

### Warning Signs
- Unresponsive staff or admissions office
- Facilities in poor condition
- Students seem unhappy or disengaged
- Lack of diversity or inclusivity concerns
- Limited resources for your interests
- High student debt with low job placement
- Poor communication or organization

### Trust Your Instincts
If something feels wrong or uncomfortable:
- Don't ignore your feelings
- Ask more questions
- Consider what the issues might mean for you
- Factor into your decision

## Making the Most of Multiple Visits

### Different Times of Year
- **Fall**: See campus in full swing
- **Winter**: Experience weather and seasonal activities
- **Spring**: See campus blooming, if applicable

### Different Types of Visits
- **General Tours**: Standard campus overview
- **Open Houses**: More detailed, multiple sessions
- **Accepted Student Days**: If accepted, special programming
- **Overnight Visits**: Experience student life more deeply

### Virtual Alternatives
If you can't visit in person:
- **Virtual Tours**: Many schools offer online options
- **Video Calls**: Schedule with admissions or students
- **Social Media**: Follow schools on various platforms
- **Student Blogs**: Read blogs from current students

College visits are your opportunity to see if a school is the right fit for you. Come prepared, ask thoughtful questions, observe carefully, and trust your instincts about where you'll thrive academically and personally.`,
    author: 'Pocketly Editorial Team',
    date: '2024-11-15',
    category: 'College Planning',
    readTime: '11 min read',
  },
];

// Ensure all articles have slugs
const mockNewsArticles = mockNewsArticlesRaw.map(article => ({
  ...article,
  slug: article.slug || generateSlug(article.title)
}));

const NewsList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch articles from Firestore, ordered by date (newest first)
        const newsRef = collection(db, 'news');
        const q = query(newsRef, orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const articlesData = [];
        querySnapshot.forEach((doc) => {
          articlesData.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        
        setArticles(articlesData);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError('Failed to load articles. Please try again later.');
        // Fallback to hardcoded articles if Firestore fails
        setArticles(mockNewsArticles);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const handleArticleClick = (slug) => {
    navigate(`/news/${slug}`);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">News & Articles</h1>
          <p className="text-lg text-gray-600">
            Stay informed about financial aid, scholarships, and college planning
          </p>
        </div>
        <div className="text-center py-12">
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    );
  }

  if (error && articles.length === 0) {
    return (
      <div className="container mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">News & Articles</h1>
          <p className="text-lg text-gray-600">
            Stay informed about financial aid, scholarships, and college planning
          </p>
        </div>
        <div className="text-center py-12">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-12">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">News & Articles</h1>
        <p className="text-lg text-gray-600">
          Stay informed about financial aid, scholarships, and college planning
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-sm">{error}</p>
        </div>
      )}

      {articles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No articles found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <Card
              key={article.id || article.slug}
              className="cursor-pointer hover:shadow-lg transition-shadow duration-300"
              onClick={() => handleArticleClick(article.slug)}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                    {article.category}
                  </span>
                  <span className="text-xs text-gray-500">{article.readTime}</span>
                </div>
                <CardTitle className="text-xl font-semibold hover:text-blue-600 transition-colors">
                  {article.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600 mb-4">
                  {article.summary}
                </CardDescription>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{article.date}</span>
                  <span>{article.author}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to parse markdown-like content into React elements
const parseArticleContent = (content) => {
  const lines = content.split('\n');
  const elements = [];
  let currentList = null;
  let currentParagraph = null;

  const processParagraph = () => {
    if (currentParagraph) {
      const text = currentParagraph.trim();
      if (text) {
        // Process inline formatting
        const processed = processInlineFormatting(text);
        elements.push(
          <p key={`p-${elements.length}`} className="text-gray-800 text-lg leading-relaxed mb-6 font-serif">
            {processed}
          </p>
        );
      }
      currentParagraph = null;
    }
  };

  const processInlineFormatting = (text) => {
    const parts = [];
    let remaining = text;
    let key = 0;

    // Process bold text (**text**)
    while (remaining.includes('**')) {
      const startIndex = remaining.indexOf('**');
      const endIndex = remaining.indexOf('**', startIndex + 2);
      
      if (endIndex === -1) break;

      // Add text before bold
      if (startIndex > 0) {
        parts.push(remaining.substring(0, startIndex));
      }

      // Add bold text
      const boldText = remaining.substring(startIndex + 2, endIndex);
      parts.push(
        <strong key={`bold-${key++}`} className="font-semibold text-gray-900">
          {boldText}
        </strong>
      );

      remaining = remaining.substring(endIndex + 2);
    }

    if (remaining) {
      parts.push(remaining);
    }

    return parts.length > 0 ? parts : [text];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    // Handle headings
    if (trimmed.startsWith('# ')) {
      processParagraph();
      if (currentList) {
        elements.push(
          <ul key={currentList.key} className="list-none space-y-3 my-6 pl-0">
            {currentList.items}
          </ul>
        );
        currentList = null;
      }
      elements.push(
        <h1 key={`h1-${index}`} className="text-4xl font-bold text-gray-900 mt-12 mb-6 leading-tight">
          {trimmed.substring(2)}
        </h1>
      );
    } else if (trimmed.startsWith('## ')) {
      processParagraph();
      if (currentList) {
        elements.push(
          <ul key={currentList.key} className="list-none space-y-3 my-6 pl-0">
            {currentList.items}
          </ul>
        );
        currentList = null;
      }
      elements.push(
        <h2 key={`h2-${index}`} className="text-3xl font-bold text-gray-900 mt-10 mb-5 leading-tight border-b border-gray-200 pb-3">
          {trimmed.substring(3)}
        </h2>
      );
    } else if (trimmed.startsWith('### ')) {
      processParagraph();
      if (currentList) {
        elements.push(
          <ul key={currentList.key} className="list-none space-y-3 my-6 pl-0">
            {currentList.items}
          </ul>
        );
        currentList = null;
      }
      elements.push(
        <h3 key={`h3-${index}`} className="text-2xl font-semibold text-gray-900 mt-8 mb-4 leading-tight">
          {trimmed.substring(4)}
        </h3>
      );
    }
    // Handle list items
    else if (trimmed.startsWith('- ')) {
      processParagraph();
      
      const listItemText = trimmed.substring(2);
      const processedText = processInlineFormatting(listItemText);
      
      const listItem = (
        <li key={`item-${index}`} className="flex items-start mb-3 pl-0">
          <span className="text-blue-600 font-bold mr-3 mt-1 text-xl">•</span>
          <span className="text-gray-800 text-lg leading-relaxed flex-1">
            {processedText}
          </span>
        </li>
      );

      if (!currentList) {
        currentList = {
          key: `list-${index}`,
          items: [listItem],
        };
      } else {
        currentList.items.push(listItem);
      }
    }
    // Handle empty lines (paragraph breaks)
    else if (!trimmed) {
      processParagraph();
      if (currentList) {
        elements.push(
          <ul key={currentList.key} className="list-none space-y-3 my-6 pl-0">
            {currentList.items}
          </ul>
        );
        currentList = null;
      }
    }
    // Regular paragraph text
    else {
      if (currentList) {
        elements.push(
          <ul key={currentList.key} className="list-none space-y-3 my-6 pl-0">
            {currentList.items}
          </ul>
        );
        currentList = null;
      }
      if (!currentParagraph) {
        currentParagraph = trimmed;
      } else {
        currentParagraph += ' ' + trimmed;
      }
    }
  });

  // Process any remaining paragraph or list
  processParagraph();
  if (currentList) {
    elements.push(
      <ul key={currentList.key} className="list-none space-y-3 my-6 pl-0">
        {currentList.items}
      </ul>
    );
  }

  return elements;
};

const NewsArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch article from Firestore by slug
        const articleRef = doc(db, 'news', slug);
        const articleSnap = await getDoc(articleRef);
        
        if (articleSnap.exists()) {
          setArticle({
            id: articleSnap.id,
            ...articleSnap.data(),
          });
        } else {
          // Fallback to hardcoded articles if not found in Firestore
          const foundArticle = mockNewsArticles.find((a) => a.slug === slug);
          if (foundArticle) {
            setArticle(foundArticle);
          } else {
            setError('Article not found.');
          }
        }
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Failed to load article. Please try again later.');
        // Fallback to hardcoded articles if Firestore fails
        const foundArticle = mockNewsArticles.find((a) => a.slug === slug);
        if (foundArticle) {
          setArticle(foundArticle);
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error && !article) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => navigate('/news')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to News
        </Button>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto px-6 py-12">
        <p>Article not found.</p>
        <Button onClick={() => navigate('/news')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to News
        </Button>
      </div>
    );
  }

  return (
    <article className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
      <Button
        onClick={() => navigate('/news')}
        variant="outline"
        className="mb-8"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to News
      </Button>

      {/* Article Header */}
      <header className="mb-8 pb-8 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <span className="inline-block px-3 py-1 text-xs font-semibold text-blue-600 uppercase tracking-wider bg-blue-50 rounded-full">
            {article.category}
          </span>
          <span className="text-sm text-gray-500">{article.readTime}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {article.title}
        </h1>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">By {article.author}</span>
          </div>
          <time className="text-gray-500" dateTime={article.date}>
            {new Date(article.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </time>
        </div>
      </header>

      {/* Article Summary/Lead */}
      <div className="mb-10 p-6 bg-gray-50 border-l-4 border-blue-600 rounded-r-lg">
        <p className="text-xl text-gray-700 leading-relaxed italic font-serif">
          {article.summary}
        </p>
      </div>

      {/* Article Content */}
      <div className="article-content">
        {parseArticleContent(article.content)}
      </div>

      {/* Article Footer */}
      <footer className="mt-16 pt-8 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-semibold">Category:</span> {article.category}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Published:</span> {new Date(article.date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <Button
            onClick={() => navigate('/news')}
            variant="outline"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to News
          </Button>
        </div>
      </footer>
    </article>
  );
};

const News = () => {
  return (
    <Routes>
      <Route path="/" element={<NewsList />} />
      <Route path="/:slug" element={<NewsArticle />} />
    </Routes>
  );
};

export default News;
